<template>
	<page enable-flex style="background: #222;">
		<div class="flex-grow flex-center">
			<div class="image" ref="image"
				@touchstart.prevent="onTouchStart"
				@touchmove.prevent="onTouchMove"
				@touchend="onTouchEnd">
				<img :src="image" alt="">
				<div v-for="(rect, idx) in rects" :key="'r'+idx" class="rect"
					:class="{'z-sel': idx === selectedIdx}"
					:style="rectStyle(rect, idx)">
					<template v-if="idx === selectedIdx">
						<div class="handle" v-for="c in corners" :key="c"
							:class="'h-'+c"></div>
					</template>
				</div>
				<div v-if="currentRect" class="rect" :style="rectStyle(currentRect, -1)"></div>
				<div v-if="selectedIdx !== null" class="del-btn" :style="delBtnStyle"
					@touchstart.stop
					@touchend.stop="deleteRect"><i class="tf">&#xe656;</i></div>
			</div>
		</div>
		<div class="foot flex-between-center">
			<div class="foot-handle flex">
				<div class="item flex-column flex-center" :class="{'z-crt': lineStyle == 'dashed'}" @click="lineStyle = 'dashed'">
					<div style="border: 2px dashed #fff;"></div>
					<p>虚线框</p>
				</div>
				<div class="item flex-column flex-center" :class="{'z-crt': lineStyle == 'solid'}" @click="lineStyle = 'solid'">
					<div style="border: 2px solid #fff;"></div>
					<p>实线框</p>
				</div>
				<div class="item flex-column flex-center" :class="{'z-crt': colorSelect}" @click="colorSelect = !colorSelect">
					<div style="border: 2px solid #fff; border-radius: 50%;" :style="{'background': lineColor}"></div>
					<p>颜色</p>
				</div>
			</div>
			<ul v-if="colorSelect" class="color-list flex-start-center">
				<li v-for="(item, i) in colorList" :key="i" :style="{'background': item}" @click="lineColor = item; colorSelect = false;"></li>
			</ul>
			<div class="foot-button flex">
				<button @click="resetRects">重置</button>
				<button @click="saveImage">保存</button>
			</div>
		</div>
	</page>
</template>

<script>
	export default {
		props: {
			image: String
		},

		data() {
			return {
				colorList: ['#FFA200', '#FF5555', '#0D6EFE', '#2EBC78'],
				colorSelect: false,
				lineStyle: 'dashed',
				lineColor: '#FFA200',
				rects: [],
				currentRect: null,
				startX: 0,
				startY: 0,
				selectedIdx: null,
				dragMode: null,
				dragCorner: null,
				dragAnchor: null,
				corners: ['tl', 'tr', 'bl', 'br'],
				tapStart: null,
				prevSelectedIdx: null,
				moveStartRect: null
			}
		},

		computed: {
			delBtnStyle() {
				if (this.selectedIdx === null) return {};
				const r = this.rects[this.selectedIdx];
				return {
					left: (r.x + r.w / 2) + 'px',
					top: (r.y + r.h) + 'px',
					marginTop: '4px',
					transform: 'translateX(-50%)'
				};
			}
		},

		methods: {
			getImageRect() {
				const el = this.$refs.image;
				if (!el) return { left: 0, top: 0, width: 0, height: 0 };
				const r = el.getBoundingClientRect();
				return { left: r.left, top: r.top, width: r.width, height: r.height };
			},

			clamp(val, min, max) {
				return Math.max(min, Math.min(max, val));
			},

			hitTest(x, y, rect, threshold) {
				return x >= rect.x - threshold && x <= rect.x + rect.w + threshold &&
					y >= rect.y - threshold && y <= rect.y + rect.h + threshold;
			},

			hitCorner(x, y, rect, threshold) {
				const pts = {
					tl: { x: rect.x, y: rect.y },
					tr: { x: rect.x + rect.w, y: rect.y },
					bl: { x: rect.x, y: rect.y + rect.h },
					br: { x: rect.x + rect.w, y: rect.y + rect.h }
				};
				for (const key of this.corners) {
					if (Math.abs(x - pts[key].x) <= threshold && Math.abs(y - pts[key].y) <= threshold) {
						return key;
					}
				}
				return null;
			},

			onTouchStart(e) {
				const touch = e.touches[0];
				const img = this.getImageRect();
				const x = this.clamp(touch.clientX - img.left, 0, img.width);
				const y = this.clamp(touch.clientY - img.top, 0, img.height);
				this.startX = x;
				this.startY = y;
				this.tapStart = { x, y };
				this.prevSelectedIdx = this.selectedIdx;

				// 选中态：检测角柄拖拽
				if (this.selectedIdx !== null) {
					const r = this.rects[this.selectedIdx];
					const corner = this.hitCorner(x, y, r, 25);
					if (corner) {
						const oppMap = { tl: 'br', tr: 'bl', bl: 'tr', br: 'tl' };
						const opp = oppMap[corner];
						const pts = {
							tl: { x: r.x, y: r.y },
							tr: { x: r.x + r.w, y: r.y },
							bl: { x: r.x, y: r.y + r.h },
							br: { x: r.x + r.w, y: r.y + r.h }
						};
						this.dragMode = 'resize';
						this.dragCorner = corner;
						this.dragAnchor = pts[opp];
						this.currentRect = null;
						return;
					}
				}

				// 检测点击已有框体（从上层往下检测）
				for (let i = this.rects.length - 1; i >= 0; i--) {
					if (this.hitTest(x, y, this.rects[i], 5)) {
						this.selectedIdx = i;
						if (i === this.prevSelectedIdx) {
							// 已选中框体内部 → 拖拽移动
							this.dragMode = 'move';
							this.moveStartRect = { x: this.rects[i].x, y: this.rects[i].y };
						} else {
							this.dragMode = 'select';
						}
						this.currentRect = null;
						return;
					}
				}

				// 空白区域：取消选中，开始绘制新框
				this.selectedIdx = null;
				this.dragMode = 'draw';
				this.currentRect = { x, y, w: 0, h: 0 };
			},

			onTouchMove(e) {
				const touch = e.touches[0];
				const img = this.getImageRect();
				const cx = this.clamp(touch.clientX - img.left, 0, img.width);
				const cy = this.clamp(touch.clientY - img.top, 0, img.height);

				if (this.dragMode === 'resize' && this.selectedIdx !== null) {
					const anchor = this.dragAnchor;
					const newX = Math.min(anchor.x, cx);
					const newY = Math.min(anchor.y, cy);
					const newW = Math.abs(cx - anchor.x);
					const newH = Math.abs(cy - anchor.y);
					const r = this.rects[this.selectedIdx];
					this.$set(this.rects, this.selectedIdx, {
						...r, x: newX, y: newY, w: newW, h: newH
					});
					return;
				}

				if (this.dragMode === 'move' && this.selectedIdx !== null && this.moveStartRect) {
					const dx = cx - this.startX;
					const dy = cy - this.startY;
					const r = this.rects[this.selectedIdx];
					const img = this.getImageRect();
					const newX = this.clamp(this.moveStartRect.x + dx, 0, img.width - r.w);
					const newY = this.clamp(this.moveStartRect.y + dy, 0, img.height - r.h);
					this.$set(this.rects, this.selectedIdx, {
						...r, x: newX, y: newY
					});
					return;
				}

				if (this.dragMode === 'draw' && this.currentRect) {
					const x = Math.min(this.startX, cx);
					const y = Math.min(this.startY, cy);
					const w = Math.abs(cx - this.startX);
					const h = Math.abs(cy - this.startY);
					this.currentRect = { x, y, w, h };
				}
			},

			onTouchEnd() {
				// 检测删除按钮点击（基于触摸起点坐标）
				if (this.tapStart && this.prevSelectedIdx !== null &&
					this.prevSelectedIdx < this.rects.length) {
					const r = this.rects[this.prevSelectedIdx];
					const btnX = r.x + r.w / 2;
					const btnY = r.y + r.h + 16;
					const dist = Math.sqrt(
						(this.tapStart.x - btnX) ** 2 +
						(this.tapStart.y - btnY) ** 2
					);
					if (dist <= 16) {
						this.rects.splice(this.prevSelectedIdx, 1);
						this.selectedIdx = null;
						this.prevSelectedIdx = null;
						this.currentRect = null;
						this.dragMode = null;
						this.tapStart = null;
						return;
					}
				}

				if (this.dragMode === 'draw' && this.currentRect) {
					if (this.currentRect.w > 2 && this.currentRect.h > 2) {
						this.rects.push({
							x: this.currentRect.x,
							y: this.currentRect.y,
							w: this.currentRect.w,
							h: this.currentRect.h,
							style: this.lineStyle,
							color: this.lineColor
						});
					}
					this.currentRect = null;
				}
				this.dragMode = null;
				this.dragCorner = null;
				this.dragAnchor = null;
				this.tapStart = null;
				this.moveStartRect = null;
			},

			deleteRect() {
				if (this.selectedIdx !== null) {
					this.rects.splice(this.selectedIdx, 1);
					this.selectedIdx = null;
				}
			},

			resetRects() {
				this.rects = [];
				this.selectedIdx = null;
				this.currentRect = null;
				this.dragMode = null;
			},

			rectStyle(rect, idx) {
				return {
					left: rect.x + 'px',
					top: rect.y + 'px',
					width: rect.w + 'px',
					height: rect.h + 'px',
					border: '2px ' + (rect.style || this.lineStyle) + ' ' + (rect.color || this.lineColor)
				};
			},

			saveImage() {
				if (!this.rects.length) {
					this.$message({
						message: '未完成标记'
					});
					return;
				}

				const imgEl = this.$refs.image && this.$refs.image.querySelector('img');
				if (!imgEl || !imgEl.naturalWidth) return;

				const naturalW = imgEl.naturalWidth;
				const naturalH = imgEl.naturalHeight;
				const display = this.getImageRect();
				const rects = this.rects.slice();

				this.request({
					url: '/file/base64',
					data: {
						file_url: this.image
					},
					loading: true,
					success: data => {
						this.$loading();
						const img = new Image();
						img.onload = () => {
							const canvas = document.createElement('canvas');
							canvas.width = naturalW;
							canvas.height = naturalH;
							const ctx = canvas.getContext('2d');

							ctx.drawImage(img, 0, 0, naturalW, naturalH);

							const scaleX = naturalW / display.width;
							const scaleY = naturalH / display.height;
							const lineW = Math.max(2, Math.round(Math.min(naturalW, naturalH) / 500));

							rects.forEach(rect => {
								ctx.strokeStyle = rect.color;
								ctx.lineWidth = lineW;
								ctx.setLineDash(rect.style === 'dashed' ? [lineW * 4, lineW * 3] : []);
								ctx.strokeRect(
									rect.x * scaleX,
									rect.y * scaleY,
									rect.w * scaleX,
									rect.h * scaleY
								);
							});

							canvas.toBlob(blob => {
								if (!blob) return;
								const info = this.parseFile(this.image);
								const file = new File([blob], `${info.name}_${new Date().getTime()}.jpg`, { type: 'image/jpeg' });
								this.uploadConfig({
									message: true
								});
								this.$upload({
									files: [file],
									loading: true,
									success: data => {
										this.$loadOver();
										localStorage.setItem('assetsMark', data[0].path);
										this.mp.navigateBack();
									}
								});
							}, 'image/jpeg', 0.92);
						};
						img.src = 'data:image/jpeg;base64,' + data;
					}
				})
			}
		}
	}
</script>

<style lang="stylus" scoped>
	.image {
		position: relative;
		touch-action: none;

		img {
			pointer-events: none;
		}

		.rect {
			position: absolute;
			box-sizing: border-box;
			pointer-events: none;

			&.z-sel {
				z-index: 2;
			}

			.handle {
				position: absolute;
				box-sizing: border-box;
				width: .28rem;
				height: .28rem;
				background: #fff;
				border: 2px solid var(--color-main);
				border-radius: 2px;
				pointer-events: none;
				transform: translate(-50%, -50%);

				&.h-tl { left: 0; top: 0; }
				&.h-tr { left: 100%; top: 0; }
				&.h-bl { left: 0; top: 100%; }
				&.h-br { left: 100%; top: 100%; }
			}
		}

		.del-btn {
			position: absolute;
			width: .48rem;
			height: .48rem;
			background: #ff4d4f;
			color: #fff;
			border-radius: 50%;
			display: flex;
			align-items: center;
			justify-content: center;
			font-size: 12px;
			line-height: 1;
			pointer-events: auto;
			z-index: 10;
		}
	}

	.foot {
		position: relative;
		height: 1.48rem;
		padding: 0 .16rem;
		background: #000;

		&-handle {
			gap: .08rem;

			.item {
				gap: .14rem;
				width: 1.2rem;
				height: 1.2rem;
				border-radius: .08rem;
				font-size: .24rem;
				color: #999;

				&.z-crt {
					color: #fff;
					background: rgba(255,255,255,.3);
				}

				div {
					box-sizing: border-box;
					width: .56rem;
					height: .56rem;
				}
			}
		}

		&-button {
			gap: .16rem;

			button {
				width: 1rem;
				height: .72rem;
				border: none;
				border-radius: .08rem;
				font-size: .28rem;
				color: #fff;
				background: var(--color-main);

				&:nth-child(1) {
					background: #666;
				}
			}
		}

		.color-list {
			gap: .2rem;
			position: absolute;
			left: 0;
			right: 0;
			bottom: 100%;
			height: 1rem;
			padding: 0 .32rem;
			background: #000;

			li {
				box-sizing: border-box;
				width: .56rem;
				height: .56rem;
				border: 2px solid #fff;
				border-radius: 50%;
			}
		}
	}
</style>
